/** Vakansiya yaratish / tahrirlash oynasi (owner va filial menejeri). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { DRIVING_TYPES, ENDORSEMENTS, EQUIPMENT, HOME_TIME, US_STATES, type FreightType } from "@/features/drivers/types";
import { useCarrierOptions, useCreateJob, useUpdateJob } from "@/features/carriers/api";
import { HIRING_TYPES, JOB_STATUS_LABEL, type HiringType, type Job, type JobStatus, type JobWrite } from "@/features/carriers/types";
import { Button } from "@/components/ui/button";
import { Checkbox, Field, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job?: Job | null;
  /** Yangi vakansiya uchun oldindan tanlangan carrier */
  carrierId?: number;
  onSaved?: (job: Job) => void;
}

interface FormState {
  carrier: string;
  title: string;
  status: JobStatus;
  states: string[];
  min_experience_months: string;
  cdl_class: string;
  endorsements_required: string[];
  pay_text: string;
  home_time: string;
  equipment: string;
  driving_type: string;
  driver_types: HiringType[];
  freight_type: FreightType | "";
  sign_on_bonus: string;
  mvr: string;
  dui: string;
  drug_test: string;
  openings: string;
  posted_at: string;
  notes: string;
}

const EMPTY: FormState = {
  carrier: "",
  title: "",
  status: "open",
  states: [],
  min_experience_months: "12",
  cdl_class: "A",
  endorsements_required: [],
  pay_text: "",
  home_time: "",
  equipment: "",
  driving_type: "",
  driver_types: [],
  freight_type: "",
  sign_on_bonus: "",
  mvr: "",
  dui: "",
  drug_test: "",
  openings: "1",
  posted_at: new Date().toISOString().slice(0, 10),
  notes: "",
};

function fromJob(j: Job): FormState {
  return {
    carrier: String(j.carrier),
    title: j.title,
    status: j.status,
    states: j.states,
    min_experience_months: String(j.min_experience_months),
    cdl_class: j.cdl_class,
    endorsements_required: j.endorsements_required,
    pay_text: j.pay_text,
    home_time: j.home_time,
    equipment: j.equipment,
    driving_type: j.driving_type,
    driver_types: j.driver_types ?? [],
    freight_type: j.freight_type ?? "",
    sign_on_bonus: j.sign_on_bonus,
    mvr: j.requirements?.mvr ?? "",
    dui: j.requirements?.dui ?? "",
    drug_test: j.requirements?.drug_test ?? "",
    openings: String(j.openings),
    posted_at: j.posted_at ?? "",
    notes: j.notes,
  };
}

export function JobFormModal({ open, onOpenChange, job, carrierId, onSaved }: Props) {
  const { t } = useTranslation("jobs");
  const editing = !!job;
  const initial = (): FormState => (job ? fromJob(job) : { ...EMPTY, carrier: carrierId ? String(carrierId) : "" });
  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const carriers = useCarrierOptions();
  const create = useCreateJob();
  const update = useUpdateJob(job?.id ?? 0);
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      setForm(job ? fromJob(job) : { ...EMPTY, carrier: carrierId ? String(carrierId) : "" });
      setErrors({});
    }
  }, [open, job, carrierId]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const allStates = form.states.includes("ALL");

  function toggleState(code: string) {
    setForm((f) => {
      const without = f.states.filter((s) => s !== "ALL");
      return { ...f, states: without.includes(code) ? without.filter((s) => s !== code) : [...without, code] };
    });
  }
  function toggleEndorsement(name: string) {
    setForm((f) => ({
      ...f,
      endorsements_required: f.endorsements_required.includes(name) ? f.endorsements_required.filter((x) => x !== name) : [...f.endorsements_required, name],
    }));
  }

  function toPayload(): JobWrite {
    return {
      carrier: Number(form.carrier),
      title: form.title.trim(),
      status: form.status,
      states: form.states,
      min_experience_months: Number(form.min_experience_months) || 0,
      cdl_class: form.cdl_class,
      endorsements_required: form.endorsements_required,
      pay_text: form.pay_text,
      home_time: form.home_time,
      equipment: form.equipment,
      driving_type: form.driving_type,
      driver_types: form.driver_types,
      freight_type: form.freight_type,
      sign_on_bonus: form.sign_on_bonus,
      requirements: { mvr: form.mvr, dui: form.dui, drug_test: form.drug_test },
      openings: Math.max(1, Number(form.openings) || 1),
      posted_at: form.posted_at || null,
      notes: form.notes,
    };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    const local: Record<string, string[]> = {};
    if (!form.carrier) local.carrier = [t("form.chooseCarrier")];
    if (!form.title.trim()) local.title = [t("form.titleRequired")];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    try {
      const saved = editing && job ? await update.mutateAsync({ ...toPayload(), version: job.version }) : await create.mutateAsync(toPayload());
      toast.success(editing ? t("form.saved") : t("form.created"));
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) {
          toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
          onOpenChange(false);
          return;
        }
        if (err.fields) setErrors(err.fields);
        else toast.error(err.message);
      } else toast.error(t("form.saveFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("form.editTitle") : t("form.newTitle")}
      size="lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? t("common:actions.saveChanges") : t("common:actions.save")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.carrier")} required error={errors.carrier}>
            <Select value={form.carrier} onChange={(e) => set("carrier", e.target.value)} disabled={editing} aria-invalid={!!errors.carrier}>
              <option value="">{t("form.choose")}</option>
              {carriers.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.status !== "active" ? ` (${t(`common:status.${c.status}`).toLowerCase()})` : ""}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.jobTitle")} required error={errors.title} className="col-span-2">
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder={t("form.titlePlaceholder")} autoFocus aria-invalid={!!errors.title} />
          </Field>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <Field label={t("form.status")} error={errors.status}>
            <Select value={form.status} onChange={(e) => set("status", e.target.value as JobStatus)}>
              {(Object.keys(JOB_STATUS_LABEL) as JobStatus[]).map((s) => (
                <option key={s} value={s}>
                  {t(`common:status.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.openings")} error={errors.openings}>
            <NumberInput min={1} value={form.openings} onChange={(e) => set("openings", e.target.value)} />
          </Field>
          <Field label={t("form.minExperience")} error={errors.min_experience_months}>
            <NumberInput min={0} value={form.min_experience_months} onChange={(e) => set("min_experience_months", e.target.value)} />
          </Field>
          <Field label={t("form.cdlClass")} error={errors.cdl_class}>
            <Select value={form.cdl_class} onChange={(e) => set("cdl_class", e.target.value)}>
              {["A", "B", "C"].map((c) => (
                <option key={c} value={c}>
                  {t("detail.cdlClass", { cls: c })}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t("form.hiringStates")} error={errors.states} hint={allStates ? t("form.all48") : form.states.length ? form.states.join(", ") : t("form.pickStates")}>
          <div className="rounded-[10px] border border-border p-3">
            <Checkbox label={t("form.all48")} checked={allStates} onChange={(e) => set("states", e.target.checked ? ["ALL"] : [])} className="mb-2 font-semibold" />
            <div className="grid grid-cols-10 gap-x-2 gap-y-1">
              {US_STATES.map((code) => (
                <Checkbox key={code} label={code} checked={!allStates && form.states.includes(code)} disabled={allStates} onChange={() => toggleState(code)} className="text-[12px]" />
              ))}
            </div>
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.pay")} error={errors.pay_text}>
            <Input value={form.pay_text} onChange={(e) => set("pay_text", e.target.value)} placeholder={t("form.payPlaceholder")} />
          </Field>
          <Field label={t("form.homeTime")} error={errors.home_time}>
            <Select value={form.home_time} onChange={(e) => set("home_time", e.target.value)}>
              <option value="">—</option>
              {[...new Set([...HOME_TIME, "Every 2 weeks, 2 days off", "Weekends"])].map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.drivingType")} error={errors.driving_type}>
            <Select value={form.driving_type} onChange={(e) => set("driving_type", e.target.value)}>
              <option value="">—</option>
              {DRIVING_TYPES.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.equipment")} error={errors.equipment}>
            <Input list="job-equipment" value={form.equipment} onChange={(e) => set("equipment", e.target.value)} placeholder={t("form.equipmentPlaceholder")} />
            <datalist id="job-equipment">
              {EQUIPMENT.map((e) => (
                <option key={e} value={e} />
              ))}
            </datalist>
          </Field>
          <Field label={t("form.signOnBonus")} error={errors.sign_on_bonus}>
            <Input value={form.sign_on_bonus} onChange={(e) => set("sign_on_bonus", e.target.value)} placeholder={t("form.bonusPlaceholder")} />
          </Field>
          <Field label={t("form.posted")} error={errors.posted_at}>
            <Input type="date" value={form.posted_at} onChange={(e) => set("posted_at", e.target.value)} />
          </Field>
        </div>

        {/* Mijoz ishi shu ikkisi atrofida: carrier qanday driver oladi va qaysi yuk (Partners jadvali) */}
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.driverTypes")} error={errors.driver_types} className="col-span-2">
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {HIRING_TYPES.map((type) => (
                <Checkbox
                  key={type}
                  label={t(`common:hiringType.${type}`)}
                  checked={form.driver_types.includes(type)}
                  onChange={() => set("driver_types", form.driver_types.includes(type) ? form.driver_types.filter((x) => x !== type) : [...form.driver_types, type])}
                />
              ))}
            </div>
          </Field>
          <Field label={t("form.freight")} error={errors.freight_type}>
            <Select value={form.freight_type} onChange={(e) => set("freight_type", e.target.value as FreightType | "")}>
              <option value="">—</option>
              {(["amazon", "street", "both"] as const).map((f) => (
                <option key={f} value={f}>
                  {t(`common:freight.${f}`)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t("form.endorsements")} error={errors.endorsements_required}>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {ENDORSEMENTS.map((name) => (
              <Checkbox key={name} label={name} checked={form.endorsements_required.includes(name)} onChange={() => toggleEndorsement(name)} />
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.mvr")} error={errors.requirements}>
            <Input value={form.mvr} onChange={(e) => set("mvr", e.target.value)} placeholder={t("form.mvrPlaceholder")} />
          </Field>
          <Field label={t("form.dui")}>
            <Input value={form.dui} onChange={(e) => set("dui", e.target.value)} placeholder={t("form.duiPlaceholder")} />
          </Field>
          <Field label={t("form.drugTest")}>
            <Input value={form.drug_test} onChange={(e) => set("drug_test", e.target.value)} placeholder={t("form.drugPlaceholder")} />
          </Field>
        </div>

        <Field label={t("form.notes")} error={errors.notes}>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </Field>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
